import { useEffect, useCallback, useRef } from "react";
import { useAthleteMonitoringStatusesContext, useLoadingContext } from "hooks";
import useMonitoringTemplateItems from "hooks/useMonitoringTemplateItems";
import type { RagStatus } from "contexts/AthleteMonitoringStatusesContext/AthleteMonitoringStatusesContext.types";

interface UseAthleteRagStatusesProps {
  athleteIds: string[];
}

function useAthleteRagStatuses(props: UseAthleteRagStatusesProps) {
  const [ragStatusesStore, { getTemplateByAthleteIds, currentRagTemplateId }] = useAthleteMonitoringStatusesContext();
  const [, setLoading] = useLoadingContext();
  const template = useMonitoringTemplateItems();

  const refAthleteIds = useRef<string[]>([]);
  const refCurrentTemplateId = useRef<string | null>(currentRagTemplateId);

  useEffect(() => {
    if (!props.athleteIds.length) return;

    // This prevents unnecessary API calls when the same athlete IDs and template ID are used
    if (
      refAthleteIds.current.length === props.athleteIds.length &&
      refAthleteIds.current.every((id) => props.athleteIds.includes(id)) &&
      refCurrentTemplateId.current === currentRagTemplateId
    ) {
      return;
    }

    setLoading(true);
    (async () => {
      try {
        await getTemplateByAthleteIds(props.athleteIds);
      } catch (error) {
        setLoading(false);
        throw error;
      } finally {
        setLoading(false);
      }
    })();

    // Update the ref with the current athlete IDs
    // This ensures that the next time this effect runs, it can compare against the latest athlete IDs and template ID
    refAthleteIds.current = props.athleteIds;
    refCurrentTemplateId.current = currentRagTemplateId;
  }, [currentRagTemplateId, getTemplateByAthleteIds, props.athleteIds, setLoading]);

  const getAthleteRagStatuses = useCallback(
    (athleteId: string): RagStatus[] | undefined => {
      return template
        ?.map((ragMetadata) => {
          const athleteRagStatuses = ragStatusesStore[athleteId];
          const ragStatus = athleteRagStatuses && athleteRagStatuses[ragMetadata.metadataId];
          if (!ragStatus) return undefined;
          const translatedStatus = {
            ...ragStatus,
            metadataName: ragMetadata.name || ragStatus.metadataName,
          };
          return translatedStatus;
        })
        .filter(Boolean) as RagStatus[];
    },
    [ragStatusesStore, template]
  );

  return getAthleteRagStatuses;
}

export default useAthleteRagStatuses;
